/**
 * ConsentPage — J-4. 수신 동의
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Alert, Badge, Page, PageHeader, Skeleton, Switch } from '@indipia/ui-core';
import { fetchPrivacySettings, updatePrivacySettings } from '../api/consent';
import type { PrivacySettings } from '../api/consent';

// ── 동의 항목 행 ──────────────────────────────────────────────────────────────
// onToggle이 없으면 "규칙상 조작 불가"(필수 동의·선행 조건 미충족) → disabled (D-09 §15.2 .switch 상태)

function ConsentRow({
  badge,
  title,
  description,
  on,
  onToggle,
}: {
  badge?: string;
  title: string;
  description: string;
  on: boolean;
  onToggle?: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border px-6 py-5 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-2">
          {badge && <Badge status="danger">{badge}</Badge>}
          <p className="text-sm font-semibold text-text">{title}</p>
        </div>
        <p className="text-xs text-text-muted">{description}</p>
      </div>
      <Switch checked={on} onChange={onToggle} disabled={!onToggle} aria-label={title} />
    </div>
  );
}

// ── 마케팅 채널 하위 행 ───────────────────────────────────────────────────────

function ChannelRow({
  label,
  on,
  onToggle,
}: {
  label: string;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 pl-4">
      <p className="text-xs font-medium text-text-muted">{label}</p>
      <Switch checked={on} onChange={onToggle} aria-label={`마케팅 ${label} 수신`} />
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────

export function ConsentPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['privacy-settings'],
    queryFn: fetchPrivacySettings,
  });

  const mutation = useMutation({
    mutationFn: updatePrivacySettings,
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: ['privacy-settings'] });
      const prev = queryClient.getQueryData<PrivacySettings>(['privacy-settings']);
      if (prev) queryClient.setQueryData(['privacy-settings'], { ...prev, ...patch });
      return { prev };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['privacy-settings'], ctx.prev);
      toast.error('설정 변경에 실패했습니다.');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['privacy-settings'] }),
  });

  function toggle(key: 'marketingYn' | 'nightMarketingYn') {
    if (!data) return;
    mutation.mutate({ [key]: data[key] === 'Y' ? 'N' : 'Y' });
  }

  function toggleChannel(key: 'marketingPushYn' | 'marketingSmsYn' | 'marketingKakaoYn' | 'marketingEmailYn') {
    if (!data) return;
    mutation.mutate({ [key]: data[key] === 'Y' ? 'N' : 'Y' });
  }

  return (
    <Page width="md">
      <PageHeader title="수신 동의" subtitle="필수 항목 동의 여부와 마케팅 수신 동의를 관리해요" />

      {/* 안내 */}
      <Alert kind="info" title="안내" className="mb-5">
        <p>필수 항목 동의를 해제하면 서비스 이용이 제한될 수 있어, 해제 불가합니다.</p>
        <p>선택 항목은 동의하지 않아도 서비스를 이용할 수 있으나 일부 기능이 제한될 수 있어요.</p>
      </Alert>

      {/* 동의 항목 카드 */}
      <div className="overflow-hidden rounded-xl border border-border bg-surface-raised">
        {isLoading ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="border-b border-border px-6 py-5 last:border-b-0">
              <Skeleton height={36} />
            </div>
          ))
        ) : (
          <>
          <ConsentRow
            badge="필수"
            title="이용약관 동의"
            description="이용약관에 동의합니다 (개인정보 수집·이용 안내 포함)"
            on={true}
          />
          <ConsentRow
            title="마케팅 정보 수신동의"
            description="신규 기능·이벤트·프로모션 정보를 받아요"
            on={data?.marketingYn === 'Y'}
            onToggle={() => toggle('marketingYn')}
          />
          {data?.marketingYn === 'Y' && (
            <div className="border-b border-border px-6 py-3">
              <p className="mb-1 text-caption font-semibold text-text-muted">수신 채널</p>
              <ChannelRow label="SMS" on={data?.marketingSmsYn === 'Y'} onToggle={() => toggleChannel('marketingSmsYn')} />
              <ChannelRow label="알림톡" on={data?.marketingKakaoYn === 'Y'} onToggle={() => toggleChannel('marketingKakaoYn')} />
              <ChannelRow label="이메일" on={data?.marketingEmailYn === 'Y'} onToggle={() => toggleChannel('marketingEmailYn')} />
            </div>
          )}
          <ConsentRow
            title="야간 광고성 정보 수신동의"
            description="오후 9시~오전 8시에도 마케팅 정보를 받아요 (마케팅 수신동의 시에만 설정 가능)"
            on={data?.nightMarketingYn === 'Y'}
            onToggle={data?.marketingYn === 'Y' ? () => toggle('nightMarketingYn') : undefined}
          />
          </>
        )}
      </div>
    </Page>
  );
}
